import React, { useState } from 'react'
import { NotesTable } from 'components/Tables/NotesTable/NotesTable'
import NotesFilterset from 'components/Tables/NotesTable/NotesFilterset'
import { FilterIdentifier } from 'models/saved_view'
import { PillTabs } from 'components/FbUI/PillTabs'
import { CompanyModal } from '../CompanyModal'
import useApiService from 'services/api'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

interface NotesListModalProps {
  show: boolean
  onHide: () => void
  chainId?: number
  contactCompanyId?: number
  doorProxyId?: number
  companyName?: string
}

export function NotesListModal({
  show,
  onHide,
  chainId,
  contactCompanyId,
  doorProxyId,
  companyName,
}: NotesListModalProps) {
  const [companyNotesCount, setCompanyNotesCount] = useState(0)
  const [doorNotesCount, setDoorNotesCount] = useState(0)
  const [selectedTab, setSelectedTab] = useState<'company' | 'door'>('company')

  const api = useApiService()
  const { data: chainData } = chainId
    ? api.useGetChain(chainId)
    : { data: undefined }

  const effectiveContactCompanyId =
    contactCompanyId || chainData?.contact_company_id

  const isCompanyPage = !doorProxyId && (chainId || contactCompanyId)

  const COMPANY_NOTES_FILTER_IDENTIFIER =
    'COMPANY_NOTES_TABLE' as FilterIdentifier
  const DOOR_NOTES_FILTER_IDENTIFIER = 'DOOR_NOTES_TABLE' as FilterIdentifier
  const NOTES_TABLE_FILTER_IDENTIFIER = 'NOTES_TABLE' as FilterIdentifier

  const COMPANY_TABLE_KEY = `notes-modal-company-${contactCompanyId || chainId}-${COMPANY_NOTES_FILTER_IDENTIFIER}`
  const DOOR_TABLE_KEY = `notes-modal-door-${contactCompanyId || chainId}-${DOOR_NOTES_FILTER_IDENTIFIER}`
  const TABLE_KEY = `notes-modal-door-${doorProxyId || contactCompanyId || chainId}-${NOTES_TABLE_FILTER_IDENTIFIER}`

  if (isCompanyPage) {
    const formatTabTitle = (label: string, count: number) => (
      <>
        {label}{' '}
        <span className="font-normal text-fg-secondary tabular-nums">
          ({count.toLocaleString()})
        </span>
      </>
    )

    return (
      <CompanyModal
        show={show}
        onHide={onHide}
        companyName={companyName}
        modalTitle="Notes"
      >
        <TableLinkStyleProvider variant="plain">
          <div className="flex max-h-[640px] w-full flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
            <div className="shrink-0 border-b border-neutral-200 bg-white px-4 py-3">
              <PillTabs<'company' | 'door'>
                tabs={[
                  {
                    key: 'company',
                    title: formatTabTitle('Company Notes', companyNotesCount),
                  },
                  {
                    key: 'door',
                    title: formatTabTitle('Door Notes', doorNotesCount),
                  },
                ]}
                activeKey={selectedTab}
                onChange={setSelectedTab}
                idPrefix="notes-modal"
                aria-label="Notes views"
                size="lg"
                className="w-fit"
              />
            </div>

            <div
              className={`flex min-h-0 flex-1 ${selectedTab === 'company' ? '' : 'hidden'}`}
            >
              <NotesTable
                tableKey={COMPANY_TABLE_KEY}
                filterIdentifier={COMPANY_NOTES_FILTER_IDENTIFIER}
                savedPresetsIdentifier="NOTES_TABLE"
                notesFilterset={
                  <NotesFilterset
                    filterIdentifier={COMPANY_NOTES_FILTER_IDENTIFIER}
                    showDoorFilters={false}
                    contactCompanyId={effectiveContactCompanyId}
                  />
                }
                contactCompanyId={contactCompanyId}
                chainId={chainId}
                doorProxyId={doorProxyId}
                setTotalRowsCount={setCompanyNotesCount}
                enablePagination={true}
                showDoorColumns={false}
                noteType="company"
              />
            </div>

            <div
              className={`flex min-h-0 flex-1 ${selectedTab === 'door' ? '' : 'hidden'}`}
            >
              <NotesTable
                tableKey={DOOR_TABLE_KEY}
                filterIdentifier={DOOR_NOTES_FILTER_IDENTIFIER}
                defaultColumnVisibility={{
                  door_country: false,
                  door_city: false,
                  door_state: false,
                }}
                savedPresetsIdentifier="DOOR_NOTES_TABLE"
                notesFilterset={
                  <NotesFilterset
                    filterIdentifier={DOOR_NOTES_FILTER_IDENTIFIER}
                    showDoorFilters={true}
                    contactCompanyId={effectiveContactCompanyId}
                  />
                }
                contactCompanyId={contactCompanyId}
                chainId={chainId}
                doorProxyId={doorProxyId}
                setTotalRowsCount={setDoorNotesCount}
                enablePagination={true}
                showDoorColumns={true}
                noteType="door"
              />
            </div>
          </div>
        </TableLinkStyleProvider>
      </CompanyModal>
    )
  }

  // For door pages
  return (
    <CompanyModal
      show={show}
      onHide={onHide}
      companyName={companyName}
      modalTitle="Notes"
    >
      <TableLinkStyleProvider variant="plain">
        <NotesTable
          tableKey={TABLE_KEY}
          filterIdentifier={NOTES_TABLE_FILTER_IDENTIFIER}
          savedPresetsIdentifier="NOTES_TABLE"
          notesFilterset={
            <NotesFilterset
              filterIdentifier={NOTES_TABLE_FILTER_IDENTIFIER}
              showDoorFilters={false}
              contactCompanyId={effectiveContactCompanyId}
            />
          }
          contactCompanyId={contactCompanyId}
          chainId={chainId}
          doorProxyId={doorProxyId}
          setTotalRowsCount={setCompanyNotesCount}
          enablePagination={true}
          showDoorColumns={false}
          noteType={undefined}
        />
      </TableLinkStyleProvider>
    </CompanyModal>
  )
}
